import { useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { Button, Badge } from "@runcheetah/ui";
import { deriveVisualChips, getLogoPreviewCarrierFill } from "@/lib/brand-onboarding-display";

import { ConfirmReviewCard } from "./confirm-review-card";
import {
  getVisualIdentityFieldVisibility,
  type BrandProfile,
  type BrandVisualStyle,
} from "./confirm-review-data";

type SaveState = "idle" | "saving" | "error";

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const LOGO_UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

export type PrimaryOnboardingLogo = {
  id: string;
  signedUrl: string;
  label: string | null;
};

const normalizeColorValue = (value: string): string => {
  const trimmed = value.trim();
  return HEX_COLOR_PATTERN.test(trimmed) ? trimmed.toUpperCase() : "#0D1F2D";
};

function EditablePalette({
  colors,
  onSave,
}: {
  colors: string[];
  onSave: (next: string[]) => Promise<void> | void;
}) {
  const intl = useIntl();
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const addColor = async () => {
    if (!pendingColor || colors.includes(pendingColor)) return;

    setSaveState("saving");

    try {
      await onSave([...colors, pendingColor]);
      setPendingColor(null);
      setSaveState("idle");
    } catch {
      setSaveState("error");
    }
  };

  const isDuplicate = pendingColor ? colors.includes(pendingColor) : false;

  return (
    <div className="space-y-3">
      <div className="relative inline-flex">
        <input
          ref={colorInputRef}
          type="color"
          defaultValue={pendingColor ?? "#0D1F2D"}
          onChange={(event) => {
            setPendingColor(normalizeColorValue(event.target.value));
            setSaveState("idle");
          }}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
          aria-label={intl.formatMessage({
            id: "brandOnboarding.confirm.pickColor",
            defaultMessage: "Pick color",
          })}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-dashed bg-background/75 px-3 shadow-sm hover:bg-primary/5"
          onClick={() => colorInputRef.current?.click()}
          disabled={saveState === "saving"}
        >
          <Plus className="mr-1 h-4 w-4" />
          <FormattedMessage
            id="brandOnboarding.confirm.pickColorCta"
            defaultMessage="Pick a color"
          />
        </Button>
      </div>

      {pendingColor ? (
        <div className="rounded-2xl border border-border/70 bg-muted/30 p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="h-10 w-10 shrink-0 rounded-xl border border-border/70 shadow-sm"
              style={{ backgroundColor: pendingColor }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 font-mono text-xs font-medium text-foreground">
              {pendingColor}
            </span>
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={() => void addColor()}
              disabled={saveState === "saving" || isDuplicate}
            >
              {saveState === "saving" ? (
                <FormattedMessage id="common.saving" defaultMessage="Saving..." />
              ) : (
                <FormattedMessage
                  id="brandOnboarding.confirm.saveColor"
                  defaultMessage="Save color"
                />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => {
                setPendingColor(null);
                setSaveState("idle");
              }}
              disabled={saveState === "saving"}
            >
              <FormattedMessage id="brandOnboarding.confirm.cancelColor" defaultMessage="Cancel" />
            </Button>
          </div>

          {isDuplicate ? (
            <p className="mt-2 text-xs text-muted-foreground">
              <FormattedMessage
                id="brandOnboarding.confirm.duplicateColor"
                defaultMessage="That color is already in the palette."
              />
            </p>
          ) : null}

          {saveState === "error" ? (
            <p className="mt-2 text-xs text-destructive">
              <FormattedMessage
                id="brandOnboarding.confirm.colorSaveFailed"
                defaultMessage="Could not save color."
              />
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ReadOnlyPalette({
  colors,
  onRemove,
}: {
  colors: string[];
  onRemove?: (color: string) => Promise<void> | void;
}) {
  const intl = useIntl();

  return (
    <div className="flex flex-wrap gap-3">
      {colors.map((color) => (
        <div key={color} className="space-y-2 text-center">
          {onRemove ? (
            <button
              type="button"
              onClick={() => void onRemove(color)}
              data-testid={`display-swatch-${color.toLowerCase()}`}
              className="group/swatch relative h-12 w-12 overflow-hidden rounded-xl border border-border/70 shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
              style={{ backgroundColor: color }}
              title={intl.formatMessage(
                { id: "brandOnboarding.confirm.removeColor", defaultMessage: "Remove {color}" },
                { color },
              )}
            >
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-destructive/78 text-destructive-foreground opacity-0 transition-opacity group-hover/swatch:opacity-100 group-focus-visible/swatch:opacity-100">
                <Trash2 className="h-4 w-4" strokeWidth={2.4} />
              </span>
            </button>
          ) : (
            <div
              data-testid={`display-swatch-${color.toLowerCase()}`}
              className="h-12 w-12 rounded-xl border border-border/70 shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: color }}
            />
          )}
          <p className="font-mono text-[11px] text-muted-foreground">{color}</p>
        </div>
      ))}
    </div>
  );
}

export function VisualIdentityStep({
  sourceDomain,
  profile,
  visual,
  primaryLogo,
  uploadPending,
  uploadingLogoName,
  onSaveColors,
  onUploadLogo,
}: {
  sourceDomain: string;
  profile: BrandProfile;
  visual: BrandVisualStyle;
  primaryLogo: PrimaryOnboardingLogo | null;
  uploadPending: boolean;
  uploadingLogoName: string | null;
  onSaveColors: (next: string[]) => Promise<void> | void;
  onUploadLogo: (file: File) => Promise<void> | void;
}) {
  const intl = useIntl();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const brandLabel = profile.brandName?.trim() || sourceDomain;
  const logoStyle = visual.logoStyle?.trim().toLowerCase();
  const visualChips = deriveVisualChips(visual).filter(
    (chip) => chip.trim().toLowerCase() !== logoStyle,
  );
  const logoCarrierFill = getLogoPreviewCarrierFill(visual);
  const { showPaletteEditor, showPaletteSwatches } = getVisualIdentityFieldVisibility(visual);
  const detectedLogoUrl = visual.logoUrl?.trim() || null;
  const displayedLogoUrl = primaryLogo?.signedUrl ?? detectedLogoUrl;

  return (
    <div className="mx-auto w-full max-w-xl">
      <ConfirmReviewCard
        label={
          <FormattedMessage
            id="brandOnboarding.confirm.visualIdentity"
            defaultMessage="Visual identity"
          />
        }
        className="p-5"
      >
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              {displayedLogoUrl ? (
                <div
                  className="group relative flex h-20 min-w-24 items-center justify-center rounded-2xl border border-border/70 px-4 shadow-sm"
                  style={{ backgroundColor: logoCarrierFill }}
                >
                  <img
                    src={displayedLogoUrl}
                    alt={intl.formatMessage(
                      {
                        id: "brandOnboarding.confirm.brandLogoAlt",
                        defaultMessage: "{brand} logo",
                      },
                      { brand: brandLabel },
                    )}
                    data-testid="display-logo"
                    className="h-12 w-auto max-w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  data-testid="display-logo"
                  className="flex h-16 w-16 items-center justify-center rounded-2xl bg-foreground text-lg font-semibold text-background shadow-sm"
                >
                  {brandLabel.slice(0, 2).toUpperCase()}
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept={LOGO_UPLOAD_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (file) {
                    void onUploadLogo(file);
                  }
                  event.currentTarget.value = "";
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 rounded-full px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                disabled={uploadPending}
                onClick={() => logoInputRef.current?.click()}
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                {uploadingLogoName ? (
                  <FormattedMessage
                    id="brandOnboarding.confirm.uploadingLogo"
                    defaultMessage="Uploading"
                  />
                ) : displayedLogoUrl ? (
                  <FormattedMessage
                    id="brandOnboarding.confirm.replaceLogo"
                    defaultMessage="Replace logo"
                  />
                ) : (
                  <FormattedMessage
                    id="brandOnboarding.confirm.uploadLogo"
                    defaultMessage="Upload logo"
                  />
                )}
              </Button>
            </div>

            {showPaletteSwatches && !showPaletteEditor ? (
              <ReadOnlyPalette colors={visual.colorPalette ?? []} />
            ) : null}

            {showPaletteSwatches && showPaletteEditor ? (
              <ReadOnlyPalette
                colors={visual.colorPalette ?? []}
                onRemove={async (color) => {
                  await onSaveColors(
                    (visual.colorPalette ?? []).filter((entry) => entry !== color),
                  );
                }}
              />
            ) : null}
          </div>

          {showPaletteEditor ? (
            <EditablePalette colors={visual.colorPalette ?? []} onSave={onSaveColors} />
          ) : null}

          {visualChips.length ? (
            <div className="flex flex-wrap gap-2">
              {visualChips.map((chip) => (
                <Badge key={chip} variant="outline" className="rounded-full px-3 py-1">
                  {chip}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </ConfirmReviewCard>
    </div>
  );
}
